import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Icon from "./AppIcon";

/**
 * RBAC Role Definitions:
 * - User: My TRONC (coming soon), Policy (coming soon), Profile only
 * - Manager: Dashboard, Takings, Distribution, Employees, Policy (coming soon), Profile, Sync Logs
 * - TenantAdmin: Everything for their tenant
 * - GlobalAdmin: Everything except creating rules and doing distributions (enforced on backend)
 */

const ROLE_HIERARCHY = {
  User: 0,
  Manager: 1,
  TenantAdmin: 2,
  GlobalAdmin: 3,
};

/**
 * RoleGuard - Protects routes based on user role.
 * @param {string[]} allowedRoles - Array of role strings that can access this route
 * @param {string} redirectTo - Where to redirect if unauthorized
 * @param {React.ReactNode} children - The protected content
 * @param {boolean} comingSoon - If true, shows a "Coming Soon" placeholder instead of the content
 */
const RoleGuard = ({
  allowedRoles,
  redirectTo,
  children,
  comingSoon = false,
}) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user.role;
  const isAllowed = allowedRoles.includes(userRole);

  if (!isAllowed) {
    // Determine where to redirect based on role
    const fallback =
      redirectTo ||
      (userRole === "User" ? "/my-tronc" : "/dashboard-overview");
    return <Navigate to={fallback} replace />;
  }

  if (comingSoon) {
    return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Icon name="Clock" size={40} className="text-primary" />
          </div>
<h2 className="text-2xl font-heading font-bold mb-3">Coming Soon</h2>
          <p className="text-muted-foreground">
    This feature is currently under development and will be available
 soon.
          </p>
        </div>
  </div>
    );
  }

  return children;
};

/**
 * Helper to check if a role meets minimum required level
 */
export const hasMinimumRole = (userRole, minimumRole) => {
  return (ROLE_HIERARCHY[userRole] ?? 0) >= (ROLE_HIERARCHY[minimumRole] ?? 0);
};

export default RoleGuard;
